import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import type { SheetStatusCountsResponse } from "./types";
import { CellStatus } from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

const EMPTY_STATUS_COUNTS = {
  [CellStatus.QUEUED]: 0,
  [CellStatus.DISPATCHED]: 0,
  [CellStatus.RUNNING]: 0,
  [CellStatus.COMPLETED]: 0,
  [CellStatus.FAILED]: 0,
  [CellStatus.STALE]: 0,
} satisfies Record<CellStatus, number>;

const ACTIVE_CELL_STATUSES = [
  CellStatus.QUEUED,
  CellStatus.DISPATCHED,
  CellStatus.RUNNING,
] as const;

export const hasActiveSheetCells = (data: SheetStatusCountsResponse | undefined): boolean =>
  Boolean(data && ACTIVE_CELL_STATUSES.some(status => (data.status_counts[status] ?? 0) > 0));

const EMPTY_STATUS_COUNTS_RESPONSE: SheetStatusCountsResponse = {
  success: true,
  total_cells: 0,
  total_rows: 0,
  status_counts: EMPTY_STATUS_COUNTS,
  status_row_counts: EMPTY_STATUS_COUNTS,
};

const normalizeIndividualStatusCounts = (
  counts: Partial<Record<CellStatus, number>> | undefined,
  total: number,
): Record<CellStatus, number> => {
  const normalizedCounts = {
    ...EMPTY_STATUS_COUNTS,
    ...(counts ?? {}),
  };
  const normalizedTotal = Math.max(0, Math.trunc(total));

  for (const status of Object.values(CellStatus)) {
    const value = normalizedCounts[status];
    normalizedCounts[status] = Number.isFinite(value)
      ? Math.min(normalizedTotal, Math.max(0, Math.trunc(value)))
      : 0;
  }

  return normalizedCounts;
};

const normalizeStatusCounts = (
  counts: Partial<Record<CellStatus, number>> | undefined,
  totalCells: number,
): Record<CellStatus, number> => {
  const normalizedTotal = Math.max(0, Math.trunc(totalCells));
  const normalizedCounts = normalizeIndividualStatusCounts(counts, normalizedTotal);
  let overflow =
    Object.values(normalizedCounts).reduce((sum, count) => sum + count, 0) - normalizedTotal;
  if (overflow <= 0) {
    return normalizedCounts;
  }

  const reduceOverflow = (status: CellStatus) => {
    if (overflow <= 0) {
      return;
    }

    const reduction = Math.min(normalizedCounts[status], overflow);
    normalizedCounts[status] -= reduction;
    overflow -= reduction;
  };

  reduceOverflow(CellStatus.STALE);
  reduceOverflow(CellStatus.COMPLETED);
  reduceOverflow(CellStatus.FAILED);
  reduceOverflow(CellStatus.QUEUED);
  reduceOverflow(CellStatus.DISPATCHED);
  reduceOverflow(CellStatus.RUNNING);

  return normalizedCounts;
};

export const useGetSheetStatusCounts = (tableId?: string, sheetId?: string) => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);

  return useQuery<SheetStatusCountsResponse>({
    queryKey: ["smart-tables", wid, tableId, sheetId, "status-counts"],
    queryFn: async () => {
      const response = await fetch(
        ENDPOINTS.smart_table_sheet_status_counts(wid, tableId!, sheetId!),
        {
          headers: jsonAuthHeaders(userToken),
        },
      );
      const payload = (await handleResponse(response)) as SheetStatusCountsResponse;

      const totalCells = Number.isFinite(payload.total_cells)
        ? Math.max(0, payload.total_cells)
        : 0;
      const totalRows = Number.isFinite(payload.total_rows) ? Math.max(0, payload.total_rows) : 0;

      return {
        success: payload.success,
        total_cells: totalCells,
        total_rows: totalRows,
        status_counts: normalizeStatusCounts(payload.status_counts, totalCells),
        status_row_counts: normalizeIndividualStatusCounts(payload.status_row_counts, totalRows),
      };
    },
    enabled: !!workspaceId && !!tableId && !!sheetId && !!userToken,
    refetchInterval: data => (hasActiveSheetCells(data) ? 1_000 : false),
    staleTime: 2_000,
    initialData: EMPTY_STATUS_COUNTS_RESPONSE,
  });
};
